import produce from 'immer';
import { atom, useSetAtom } from 'jotai';
import {
  NotificationCountType,
  ClientEvent,
  IRoomTimelineData,
  MatrixClient,
  MatrixEvent,
  Room,
  RoomEvent,
  SyncState,
} from 'matrix-js-sdk';
import { ReceiptContent, ReceiptType } from 'matrix-js-sdk/lib/@types/read_receipts';
import { MAIN_ROOM_TIMELINE } from 'matrix-js-sdk/lib/@types/read_receipts';
import { useCallback, useEffect } from 'react';
import {
  Membership,
  NotificationType,
  RoomToUnread,
  UnreadInfo,
  Unread,
  StateEvent,
} from '../../../types/matrix/room';
import {
  getAllParents,
  getNotificationType,
  getUnreadInfo,
  getUnreadInfos,
  isNotificationEvent,
  roomHaveUnread,
} from '../../utils/room';
import { roomToParentsAtom } from './roomToParents';
import { useStateEventCallback } from '../../hooks/useStateEventCallback';
import { useSyncState } from '../../hooks/useSyncState';
import { useRoomsNotificationPreferencesContext } from '../../hooks/useRoomsNotificationPreferences';

export type RoomToUnreadAction =
  | {
      type: 'RESET';
      unreadInfos: UnreadInfo[];
    }
  | {
      type: 'PUT';
      unreadInfo: UnreadInfo;
    }
  | {
      type: 'DELETE';
      roomId: string;
    };

export const unreadInfoToUnread = (unreadInfo: UnreadInfo): Unread => ({
  highlight: unreadInfo.highlight,
  total: unreadInfo.total,
  from: null,
});

type ReceiptContext = {
  threadId?: string;
  unthreaded: boolean;
};

const getUnreadInfoFromCounts = (room: Room, total: number, highlight: number): UnreadInfo => ({
  roomId: room.roomId,
  highlight,
  total: highlight > total ? highlight : total,
});

const getSelfReceiptContexts = (content: ReceiptContent, userId: string): ReceiptContext[] => {
  const receiptContexts: ReceiptContext[] = [];

  Object.keys(content).forEach((eventId) => {
    (Object.keys(content[eventId]) as ReceiptType[]).forEach((receiptType) => {
      const receipt = content[eventId][receiptType][userId];
      if (!receipt) return;

      receiptContexts.push({
        threadId: receipt.thread_id,
        unthreaded: !receipt.thread_id,
      });
    });
  });

  return receiptContexts;
};

export const getOptimisticReceiptUnreadInfo = (
  room: Room,
  receiptContexts: ReceiptContext[]
): UnreadInfo | undefined => {
  if (receiptContexts.length === 0) return undefined;
  if (receiptContexts.some((context) => context.unthreaded)) {
    return getUnreadInfoFromCounts(room, 0, 0);
  }

  let total = room.getUnreadNotificationCount(NotificationCountType.Total);
  let highlight = room.getUnreadNotificationCount(NotificationCountType.Highlight);
  const hasMainReceipt = receiptContexts.some((context) => context.threadId === MAIN_ROOM_TIMELINE);
  if (hasMainReceipt) {
    total -= room.getRoomUnreadNotificationCount(NotificationCountType.Total);
    highlight -= room.getRoomUnreadNotificationCount(NotificationCountType.Highlight);
  }

  const threadIds = new Set(
    receiptContexts
      .map((context) => context.threadId)
      .filter((threadId): threadId is string => !!threadId && threadId !== MAIN_ROOM_TIMELINE)
  );
  threadIds.forEach((threadId) => {
    total -= room.getThreadUnreadNotificationCount(threadId, NotificationCountType.Total);
    highlight -= room.getThreadUnreadNotificationCount(threadId, NotificationCountType.Highlight);
  });

  return getUnreadInfoFromCounts(room, Math.max(0, total), Math.max(0, highlight));
};

export const getRoomUnreadAction = (
  mx: MatrixClient,
  room: Room,
  unreadInfo: UnreadInfo = getUnreadInfo(room)
): Extract<RoomToUnreadAction, { type: 'PUT' | 'DELETE' }> => {
  if (
    room.isSpaceRoom() ||
    room.getMyMembership() !== Membership.Join ||
    getNotificationType(mx, room.roomId) === NotificationType.Mute
  ) {
    return {
      type: 'DELETE',
      roomId: room.roomId,
    };
  }

  if (unreadInfo.total > 0 || unreadInfo.highlight > 0 || roomHaveUnread(mx, room)) {
    return {
      type: 'PUT',
      unreadInfo,
    };
  }

  return {
    type: 'DELETE',
    roomId: room.roomId,
  };
};

const putUnreadInfo = (
  roomToUnread: RoomToUnread,
  allParents: Set<string>,
  unreadInfo: UnreadInfo
) => {
  const oldUnread = roomToUnread.get(unreadInfo.roomId) ?? { highlight: 0, total: 0, from: null };
  roomToUnread.set(unreadInfo.roomId, unreadInfoToUnread(unreadInfo));

  const newH = unreadInfo.highlight - oldUnread.highlight;
  const newT = unreadInfo.total - oldUnread.total;

  allParents.forEach((parentId) => {
    const oldParentUnread = roomToUnread.get(parentId) ?? { highlight: 0, total: 0, from: null };
    roomToUnread.set(parentId, {
      highlight: (oldParentUnread.highlight += newH),
      total: (oldParentUnread.total += newT),
      from: new Set([...(oldParentUnread.from ?? []), unreadInfo.roomId]),
    });
  });
};

const deleteUnreadInfo = (roomToUnread: RoomToUnread, allParents: Set<string>, roomId: string) => {
  const oldUnread = roomToUnread.get(roomId);
  if (!oldUnread) return;
  roomToUnread.delete(roomId);

  allParents.forEach((parentId) => {
    const oldParentUnread = roomToUnread.get(parentId);
    if (!oldParentUnread) return;
    const newFrom = new Set([...(oldParentUnread.from ?? roomId)]);
    newFrom.delete(roomId);
    if (newFrom.size === 0) {
      roomToUnread.delete(parentId);
      return;
    }
    roomToUnread.set(parentId, {
      highlight: oldParentUnread.highlight - oldUnread.highlight,
      total: oldParentUnread.total - oldUnread.total,
      from: newFrom,
    });
  });
};

export const unreadEqual = (u1: Unread, u2: Unread): boolean => {
  const countEqual = u1.highlight === u2.highlight && u1.total === u2.total;

  if (!countEqual) return false;

  const f1 = u1.from;
  const f2 = u2.from;
  if (f1 === null && f2 === null) return true;
  if (f1 === null || f2 === null) return false;

  if (f1.size !== f2.size) return false;

  let fromEqual = true;
  f1?.forEach((item) => {
    if (!f2?.has(item)) {
      fromEqual = false;
    }
  });

  return fromEqual;
};

const baseRoomToUnread = atom<RoomToUnread>(new Map());
export const roomToUnreadAtom = atom<RoomToUnread, [RoomToUnreadAction], undefined>(
  (get) => get(baseRoomToUnread),
  (get, set, action) => {
    if (action.type === 'RESET') {
      const draftRoomToUnread: RoomToUnread = new Map();
      action.unreadInfos.forEach((unreadInfo) => {
        putUnreadInfo(
          draftRoomToUnread,
          getAllParents(get(roomToParentsAtom), unreadInfo.roomId),
          unreadInfo
        );
      });
      set(baseRoomToUnread, draftRoomToUnread);
      return;
    }
    if (action.type === 'PUT') {
      const { unreadInfo } = action;
      const currentUnread = get(baseRoomToUnread).get(unreadInfo.roomId);
      if (currentUnread && unreadEqual(currentUnread, unreadInfoToUnread(unreadInfo))) {
        // Do not update if unread data has not changes
        // like total & highlight
        return;
      }
      set(
        baseRoomToUnread,
        produce(get(baseRoomToUnread), (draftRoomToUnread) =>
          putUnreadInfo(
            draftRoomToUnread,
            getAllParents(get(roomToParentsAtom), unreadInfo.roomId),
            unreadInfo
          )
        )
      );
      return;
    }
    if (action.type === 'DELETE' && get(baseRoomToUnread).has(action.roomId)) {
      set(
        baseRoomToUnread,
        produce(get(baseRoomToUnread), (draftRoomToUnread) =>
          deleteUnreadInfo(
            draftRoomToUnread,
            getAllParents(get(roomToParentsAtom), action.roomId),
            action.roomId
          )
        )
      );
    }
  }
);

export const useBindRoomToUnreadAtom = (mx: MatrixClient, unreadAtom: typeof roomToUnreadAtom) => {
  const setUnreadAtom = useSetAtom(unreadAtom);
  const roomsNotificationPreferences = useRoomsNotificationPreferencesContext();
  const syncRoomUnread = useCallback(
    (room: Room, unreadInfo?: UnreadInfo) => {
      setUnreadAtom(getRoomUnreadAction(mx, room, unreadInfo));
    },
    [mx, setUnreadAtom]
  );

  useEffect(() => {
    setUnreadAtom({
      type: 'RESET',
      unreadInfos: getUnreadInfos(mx),
    });
  }, [mx, setUnreadAtom]);

  useSyncState(
    mx,
    useCallback(
      (state, prevState) => {
        if (
          (state === SyncState.Prepared && prevState === null) ||
          (state === SyncState.Syncing && prevState !== SyncState.Syncing)
        ) {
          setUnreadAtom({
            type: 'RESET',
            unreadInfos: getUnreadInfos(mx),
          });
        }
      },
      [mx, setUnreadAtom]
    )
  );

  useEffect(() => {
    const handleTimelineEvent = (
      mEvent: MatrixEvent,
      room: Room | undefined,
      toStartOfTimeline: boolean | undefined,
      removed: boolean,
      data: IRoomTimelineData
    ) => {
      if (!room || !data.liveEvent || removed || !isNotificationEvent(mEvent)) return;
      if (mEvent.getSender() === mx.getUserId()) return;
      syncRoomUnread(room);
    };
    mx.on(RoomEvent.Timeline, handleTimelineEvent);
    return () => {
      mx.removeListener(RoomEvent.Timeline, handleTimelineEvent);
    };
  }, [mx, syncRoomUnread]);

  useEffect(() => {
    const handleReceipt = (mEvent: MatrixEvent, room: Room) => {
      const myUserId = mx.getUserId();
      if (!myUserId) return;
      if (room.isSpaceRoom()) return;
      const content = mEvent.getContent<ReceiptContent>();

      const receiptContexts = getSelfReceiptContexts(content, myUserId);
      if (receiptContexts.length > 0) {
        syncRoomUnread(room, getOptimisticReceiptUnreadInfo(room, receiptContexts));
      }
    };
    mx.on(RoomEvent.Receipt, handleReceipt);
    return () => {
      mx.removeListener(RoomEvent.Receipt, handleReceipt);
    };
  }, [mx, syncRoomUnread]);

  useEffect(() => {
    const roomHandlers = new Map<Room, () => void>();

    const attachRoom = (room: Room) => {
      if (roomHandlers.has(room)) return;

      const handleUnreadNotifications = () => {
        syncRoomUnread(room);
      };

      room.on(RoomEvent.UnreadNotifications, handleUnreadNotifications);
      roomHandlers.set(room, handleUnreadNotifications);
    };

    mx.getRooms().forEach(attachRoom);
    mx.on(ClientEvent.Room, attachRoom);

    return () => {
      mx.removeListener(ClientEvent.Room, attachRoom);
      roomHandlers.forEach((handler, room) => {
        room.removeListener(RoomEvent.UnreadNotifications, handler);
      });
    };
  }, [mx, syncRoomUnread]);

  useEffect(() => {
    setUnreadAtom({
      type: 'RESET',
      unreadInfos: getUnreadInfos(mx),
    });
  }, [mx, setUnreadAtom, roomsNotificationPreferences]);

  useEffect(() => {
    const handleMembershipChange = (room: Room, membership: string) => {
      if (membership !== Membership.Join) {
        setUnreadAtom({
          type: 'DELETE',
          roomId: room.roomId,
        });
      }
    };
    mx.on(RoomEvent.MyMembership, handleMembershipChange);
    return () => {
      mx.removeListener(RoomEvent.MyMembership, handleMembershipChange);
    };
  }, [mx, setUnreadAtom]);

  useStateEventCallback(
    mx,
    useCallback(
      (mEvent) => {
        if (mEvent.getType() === StateEvent.SpaceChild) {
          setUnreadAtom({
            type: 'RESET',
            unreadInfos: getUnreadInfos(mx),
          });
        }
      },
      [mx, setUnreadAtom]
    )
  );
};
